import React, { useMemo } from 'react';

import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';

import { useQueryGetProjectStatusScoreCards } from 'api/queries/getProjectStatusScoreCards.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { IScoreCardsWrapperProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { LoaderContainer, WidgetCardsWrapper } from 'components/common';
import { CardsSection } from 'components/widgets/ProjectSummary/CardsSection';

const title = 'Project Summary';
const componentKey = 'projectSummary';
const id = 'project-summary';

const ScoreCardsComponent = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    originalRoute = '',
    filter,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
}: IScoreCardsWrapperProps) => {
    const { isGenerativeAi } = usePinboardWidgetWrapperContext();
    const filters = useMemo(
        () =>
            widgetId
                ? filter?.filters || []
                : getFiltersData({
                      [FilterNames.ACTIVITY_CODES]: globalFilterData?.activityCodes
                          ?.filter((item) => !item.value.isExclude)
                          .map((i) => i.value.name),
                      [FilterNames.ACTIVITY_CODES_EXCLUDE]: globalFilterData?.activityCodes
                          ?.filter((item) => item.value.isExclude)
                          .map((i) => i.value.name),
                      [FilterNames.FRAGNET_SEARCH]: globalFilterData?.milestoneFragnet,
                      [FilterNames.TAG_CARD_BOARD]: globalFilterData?.tags
                          ?.filter((item) => !item.value.isExclude)
                          .map((i) => i.value.name),
                      [FilterNames.TAG_CARD_BOARD_EXCLUDE]: globalFilterData?.tags
                          ?.filter((item) => item.value.isExclude)
                          .map((i) => i.value.name),
                      [FilterNames.WBS_SEARCH]: globalFilterData?.wbs,
                  }),

        [filter, globalFilterData],
    );

    const { isLoading, data: scoreCardsData } = useQueryGetProjectStatusScoreCards({
        compareVersionId,
        latestVersionId,
        contractId,
        filter: {
            filters,
        },
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId),
    });
    const qualityCardData = useMemo(
        () => scoreCardsData?.summaryCards.find((card) => card?.cardTitle.cardType === 'QualityCard'),
        [scoreCardsData],
    );
    const progressCardData = useMemo(
        () => scoreCardsData?.summaryCards.find((card) => card?.cardTitle.cardType === 'ProgressCard'),
        [scoreCardsData],
    );

    const riskCardData = useMemo(
        () => scoreCardsData?.summaryCards.find((card) => card?.cardTitle.cardType === 'RiskCard'),
        [scoreCardsData],
    );

    const showNoData =
        !riskCardData?.cardProperties && !qualityCardData?.cardProperties && !progressCardData?.cardProperties;

    return (
        <WidgetCardsWrapper
            projectId={projectId}
            contractId={contractId}
            latestVersionId={latestVersionId}
            title={title}
            componentKey={componentKey}
            tooltipText={scoreCardsData?.info}
            isLoading={isLoading}
            widgetId={widgetId}
            originalRoute={originalRoute}
            filter={{ filters }}
            id={id}
            externalComponents={externalComponents}
            editNarrative={editNarrative}
            setSubTitleForExport={setSubTitleForExport}
        >
            {isLoading && !scoreCardsData && (
                <LoaderContainer>
                    <OverlayWithSpinner />
                </LoaderContainer>
            )}
            {(!isLoading && !scoreCardsData) || (showNoData && <NoData />)}
            {scoreCardsData && (
                <CardsSection
                    progressCardData={progressCardData}
                    riskCardData={riskCardData}
                    qualityCardData={qualityCardData}
                />
            )}
        </WidgetCardsWrapper>
    );
};
export default React.memo(ScoreCardsComponent);
